/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useLivestreams } from '@sportninja/common/hooks/useLivestreams'
import { useLivestreamMutations } from '@sportninja/common/hooks/useLivestreamMutations'
import type {
  Livestream,
  LivestreamType,
} from '@sportninja/common/types/Livestream'
import { Alert, App, Spin, Table } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { Container } from 'src/components/Layout'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'
import dayjs from 'dayjs'
import DeleteLivestreamModal from './DeleteLivestreamModal'
import LivestreamDetailsModal from './LivestreamDetailsModal'
import VideoAssignment from '../VideoAssignment'
import PersonAddIcon from 'src/components/icons/PersonAddIcon'
import VideoAddIcon from 'src/components/icons/VideoAddIcon'
import ViewIcon from 'src/components/icons/ViewIcon'
import DeleteIcon from 'src/components/icons/DeleteIcon'

const PLACEHOLDER_IMAGE = '/images/generic_placeholders/placeholder_video.png'

const EmptyLivestreamState: React.FC = () => (
  <div
    css={css`
      border: 1px solid ${colors.SECONDARY_200};
      border-radius: 8px;
      overflow: hidden;
    `}
  >
    <div
      css={css`
        background-color: ${colors.SECONDARY_300};
        padding: 8px 16px;
        display: flex;
        align-items: center;
      `}
    >
      <span
        css={css`
          color: ${colors.NEUTRAL_70};
          font-size: 12px;
          text-transform: uppercase;
          font-weight: 500;
        `}
      >
        Preview
      </span>
    </div>
    <div
      css={css`
        background-color: ${colors.SECONDARY_500};
        padding: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
      `}
    >
      <span
        css={css`
          color: ${colors.NEUTRAL_50};
          font-size: 14px;
        `}
      >
        No Livestreams Configured
      </span>
    </div>
  </div>
)

interface LivestreamsProps {
  gameId: string
  homeTeamName: string
  visitorTeamName: string
  onLivestreamDeleted?: () => Promise<void>
  homeRosterId?: string | null
  visitorRosterId?: string | null
  homeTeamImage?: string | null
  visitorTeamImage?: string | null
  hasVideographerPermission?: boolean
  refreshKey?: number
  onAddLivestreamClick?: () => void
  onAddVideographerClick?: () => void
}

const statusConfig = {
  active: {
    color: colors.SUCCESS_200,
    bgColor: 'rgba(2, 47, 4, 0.3)',
    borderColor: colors.SUCCESS_200,
    label: 'Live',
  },
  idle: {
    color: colors.WARNING_200,
    bgColor: 'rgba(58, 41, 5, 0.3)',
    borderColor: colors.WARNING_200,
    label: 'Idle',
  },
  disabled: {
    color: colors.NEUTRAL_70,
    bgColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: colors.NEUTRAL_70,
    label: 'Disabled',
  },
}

const providerMap: Record<number, string> = {
  1: 'Bambuser',
  2: 'Video Upload',
  3: 'Mux',
  4: 'Pixellot',
}

const typeLabels: Record<LivestreamType, string> = {
  league: 'League',
  home: 'Home Team',
  visitor: 'Visitor Team',
}

type TabType = 'videographers' | 'livestreams'

const Livestreams: React.FC<LivestreamsProps> = ({
  gameId,
  homeTeamName,
  visitorTeamName,
  onLivestreamDeleted,
  homeRosterId,
  visitorRosterId,
  hasVideographerPermission,
  refreshKey,
  onAddLivestreamClick,
  onAddVideographerClick,
}) => {
  const { message } = App.useApp()
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])

  // Tab state - default to videographers if user has permission, otherwise livestreams
  const [activeTab, setActiveTab] = useState<TabType>('livestreams')

  const { livestreams, loading, error, refetch } = useLivestreams(gameId)
  const { deleteLivestream, loading: deleteLoading } =
    useLivestreamMutations(gameId)

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Livestream | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedLivestream, setSelectedLivestream] =
    useState<Livestream | null>(null)

  const handleDeleteClick = useCallback((livestream: Livestream) => {
    setDeleteTarget(livestream)
    setDeleteError(null)
    setDeleteModalOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) {
      return
    }

    setDeleteError(null)

    try {
      await deleteLivestream(deleteTarget.type)
      await refetch()
      // Notify parent to refresh header livestream state
      if (onLivestreamDeleted) {
        await onLivestreamDeleted()
      }
      setDeleteModalOpen(false)
      setDeleteTarget(null)
      message.success('Livestream deleted successfully')
    } catch (e) {
      const err = e as Error
      setDeleteError(err.message || 'Failed to delete livestream')
    }
  }, [deleteTarget, deleteLivestream, refetch, message, onLivestreamDeleted])

  const handleDeleteClose = useCallback(() => {
    if (!deleteLoading) {
      setDeleteModalOpen(false)
      setDeleteTarget(null)
      setDeleteError(null)
    }
  }, [deleteLoading])

  const handleViewClick = useCallback((livestream: Livestream) => {
    setSelectedLivestream(livestream)
    setDetailsModalOpen(true)
  }, [])

  const handleDetailsClose = useCallback(() => {
    setDetailsModalOpen(false)
    setSelectedLivestream(null)
  }, [])

  const getTypeLabel = useCallback(
    (type: LivestreamType) => {
      if (type === 'home') {
        return homeTeamName || typeLabels.home
      }
      if (type === 'visitor') {
        return visitorTeamName || typeLabels.visitor
      }
      return typeLabels[type]
    },
    [homeTeamName, visitorTeamName]
  )

  const columns = useMemo(
    () => [
      {
        title: 'Preview',
        key: 'preview',
        width: 120,
        render: (_: unknown, item: Livestream) => (
          <div
            css={css`
              width: 120px;
              aspect-ratio: 16/9;
              border-radius: 8px;
              overflow: hidden;
              background: ${colors.SECONDARY_400};
              cursor: pointer;
              position: relative;
              &:hover {
                opacity: 0.8;
              }
            `}
            onClick={() => handleViewClick(item)}
            title='Click to view details'
          >
            {item.stream_url ? (
              <video
                src={item.stream_url}
                css={css`
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                `}
                muted
                preload='metadata'
                onError={(e) => {
                  const target = e.currentTarget
                  target.style.display = 'none'
                  const parent = target.parentElement
                  if (parent) {
                    const img = document.createElement('img')
                    img.src = PLACEHOLDER_IMAGE
                    img.style.width = '100%'
                    img.style.height = '100%'
                    img.style.objectFit = 'cover'
                    parent.appendChild(img)
                  }
                }}
              />
            ) : (
              <img
                src={PLACEHOLDER_IMAGE}
                alt='No video'
                css={css`
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                `}
              />
            )}
          </div>
        ),
      },
      {
        title: 'Type',
        key: 'type',
        width: 180,
        render: (_: unknown, item: Livestream) => {
          const isTeamType = item.type === 'home' || item.type === 'visitor'
          const typeLabel = typeLabels[item.type] || item.type
          const teamName =
            item.type === 'home'
              ? homeTeamName
              : item.type === 'visitor'
              ? visitorTeamName
              : null

          return (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 2px;
              `}
            >
              <span
                css={css`
                  color: ${colors.WHITE};
                  font-weight: 500;
                `}
              >
                {typeLabel}
              </span>
              {isTeamType && teamName && (
                <span
                  css={css`
                    color: ${colors.NEUTRAL_70};
                    font-size: 12px;
                  `}
                >
                  {teamName}
                </span>
              )}
            </div>
          )
        },
      },
      {
        title: 'Provider',
        key: 'provider',
        width: 120,
        render: (_: unknown, item: Livestream) => (
          <span
            css={css`
              color: ${colors.NEUTRAL_70};
            `}
          >
            {providerMap[item.video_provider_type_id] || 'Unknown'}
          </span>
        ),
      },
      {
        title: 'Created',
        key: 'created_at',
        width: 180,
        render: (_: unknown, item: Livestream) => (
          <span
            css={css`
              color: ${colors.NEUTRAL_70};
            `}
          >
            {dayjs(item.created_at).format('MMM DD, YYYY - HH:mm')}
          </span>
        ),
      },
      {
        title: 'Updated',
        key: 'updated_at',
        width: 180,
        render: (_: unknown, item: Livestream) => (
          <span
            css={css`
              color: ${colors.NEUTRAL_70};
            `}
          >
            {item.updated_at
              ? dayjs(item.updated_at).format('MMM DD, YYYY - HH:mm')
              : '-'}
          </span>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 128,
        render: (_: unknown, item: Livestream) => {
          const config = statusConfig[item.status] || statusConfig.idle
          return (
            <span
              css={css`
                display: inline-block;
                padding: 2px 8px;
                border: 1px solid ${config.borderColor};
                background-color: ${config.bgColor};
                border-radius: 4px;
                color: ${config.color};
                font-size: 12px;
                font-weight: 500;
              `}
            >
              {config.label}
            </span>
          )
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, item: Livestream) => (
          <div
            css={css`
              display: flex;
              gap: 16px;
            `}
          >
            <button
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 4px;
                color: ${colors.PRIMARY};
                transition: opacity 0.2s;
                &:hover {
                  opacity: 0.7;
                }
              `}
              onClick={() => handleViewClick(item)}
              title='View details'
            >
              <ViewIcon size={24} color={colors.PRIMARY} />
            </button>
            <button
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 4px;
                transition: opacity 0.2s;
                &:hover {
                  opacity: 0.7;
                }
              `}
              onClick={() => handleDeleteClick(item)}
              title='Delete livestream'
            >
              <DeleteIcon size={24} color={colors.ERROR_200} />
            </button>
          </div>
        ),
      },
    ],
    [handleDeleteClick, handleViewClick, homeTeamName, visitorTeamName]
  )

  if (error && !loading) {
    return (
      // @ts-expect-error Container is a JavaScript component without TypeScript types
      <Container
        noWrap
        column
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin: 0 8px;
        `}
      >
        <Alert
          message='Error'
          description={error.message}
          type='error'
          showIcon
        />
      </Container>
    )
  }

  return (
    // @ts-expect-error Container is a JavaScript component without TypeScript types
    <Container
      noWrap
      column
      css={css`
        min-height: 100vh;
        width: 100%;
        margin: 0 8px 24px 8px;
        .list-container {
          flex: 1;
        }

        .list-inner {
          margin-left: 0;
          margin-right: 0;
        }
      `}
    >
      {/* Tab Navigation - only show if user has videographer permission */}
      {hasVideographerPermission && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 16px;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0px;
              background-color: ${colors.SECONDARY_500};
              border: 1px solid ${colors.SECONDARY_300};
              border-radius: 8px;
              height: 40px;
              width: fit-content;
            `}
          >
            {[
              { value: 'livestreams' as TabType, label: 'Livestreams' },
              { value: 'videographers' as TabType, label: 'Assignments' },
            ].map((tab, index) => {
              const isActive = activeTab === tab.value
              const isFirst = index === 0
              const isLast = index === 1

              return (
                <button
                  key={tab.value}
                  type='button'
                  onClick={() => setActiveTab(tab.value)}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px 24px;
                    height: 40px;
                    border-radius: ${isFirst
                      ? '8px 0 0 8px'
                      : isLast
                      ? '0 8px 8px 0'
                      : '0'};
                    background-color: ${isActive
                      ? colors.SECONDARY_300
                      : 'transparent'};
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 14px;
                    font-weight: ${isActive ? 700 : 400};
                    line-height: 20.3px;
                    color: ${isActive ? colors.NEUTRAL_0 : colors.NEUTRAL_50};

                    &:hover {
                      background-color: ${isActive
                        ? colors.SECONDARY_300
                        : colors.SECONDARY_200};
                    }

                    &:active {
                      transform: scale(0.98);
                    }
                  `}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Action Button - context-aware based on active tab */}
          {activeTab === 'videographers' && onAddVideographerClick && (
            <button
              type='button'
              onClick={onAddVideographerClick}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                height: 32px;
                background-color: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: 500;
                line-height: 20.3px;
                color: ${colors.SECONDARY_300};

                &:hover {
                  background-color: ${colors.PRIMARY_400};
                }

                &:active {
                  transform: scale(0.98);
                }
              `}
            >
              <PersonAddIcon size={24} color={colors.SECONDARY_300} />
              Add Videographer
            </button>
          )}
          {activeTab === 'livestreams' && onAddLivestreamClick && (
            <button
              type='button'
              onClick={onAddLivestreamClick}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                height: 32px;
                background-color: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: 500;
                line-height: 20.3px;
                color: ${colors.SECONDARY_300};

                &:hover {
                  background-color: ${colors.PRIMARY_400};
                }

                &:active {
                  transform: scale(0.98);
                }
              `}
            >
              <VideoAddIcon size={24} color={colors.SECONDARY_300} />
              Add Livestream
            </button>
          )}
        </div>
      )}

      {/* Videographers Section */}
      {hasVideographerPermission && activeTab === 'videographers' && (
        <VideoAssignment
          gameId={gameId}
          homeRosterId={homeRosterId || null}
          visitorRosterId={visitorRosterId || null}
          homeTeamName={homeTeamName}
          visitorTeamName={visitorTeamName}
          refreshKey={refreshKey}
          embedded
        />
      )}

      {/* Livestreams Section */}
      {(activeTab === 'livestreams' || !hasVideographerPermission) && (
        <>
          {loading ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 200px;
              `}
            >
              <Spin
                size='large'
                css={css`
                  .ant-spin-dot-item {
                    background-color: ${colors.PRIMARY};
                  }
                `}
              />
            </div>
          ) : livestreams.length > 0 ? (
            <Table
              columns={columns}
              dataSource={livestreams}
              rowKey={(record) => record.id}
              pagination={false}
              size='small'
              scroll={{ x: isMobile ? 600 : undefined }}
              css={getTableStyles(isMobile)}
            />
          ) : (
            <EmptyLivestreamState />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteLivestreamModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        livestreamType={deleteTarget?.type || null}
        typeLabel={deleteTarget ? getTypeLabel(deleteTarget.type) : ''}
        isLoading={deleteLoading}
        error={deleteError}
      />

      {/* Livestream Details Modal */}
      <LivestreamDetailsModal
        isOpen={detailsModalOpen}
        onClose={handleDetailsClose}
        livestream={selectedLivestream}
        typeLabel={
          selectedLivestream ? getTypeLabel(selectedLivestream.type) : ''
        }
        gameId={gameId}
      />
    </Container>
  )
}

export default Livestreams
